// Plafond assicurativo: cella tabella, richiesta plafond, import Excel assicurazione, lista richieste admin
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, ShieldCheck, Send, Check, FileSpreadsheet, UserPlus } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const fmEur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmDate = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "");

export const PlafondCell = ({ info, testId }) => {
  if (!info) return <span className="text-stone-300 text-xs">—</span>;
  const st = info.insurance_status;
  if (st === "refused" || st === "revoked") {
    if (info.advance_payment_exempt) {
      return (
        <div className="text-xs leading-tight whitespace-nowrap" data-testid={testId} title={`Eccezione aziendale impostata da ${info.advance_payment_exempt_by || "admin"}${info.advance_payment_exempt_at ? ` il ${fmDate(info.advance_payment_exempt_at)}` : ""}`}>
          <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold whitespace-nowrap">NON CONCESSO · ESENTE</Badge>
          <p className="text-[10px] text-stone-500 mt-0.5">nessun pagamento anticipato (eccezione)</p>
        </div>
      );
    }
    return <Badge className="bg-red-600 text-white text-[10px] font-bold whitespace-nowrap" data-testid={testId}>NON CONCESSO · PAG. ANTICIPATO</Badge>;
  }
  if ((info.credit_limit || 0) > 0) {
    const over = info.open_amount > info.credit_limit;
    return (
      <div className="text-xs leading-tight whitespace-nowrap" data-testid={testId}>
        <p className="font-bold text-emerald-700">{fmEur(info.credit_limit)} concesso</p>
        <p className={over ? "text-red-600 font-bold" : "text-stone-500"}>
          usato {fmEur(info.open_amount)}{over ? " ⚠ SFORATO" : ` · disp. ${fmEur(info.credit_limit - info.open_amount)}`}
        </p>
      </div>
    );
  }
  if (info.pending_request) return <Badge className="bg-sky-100 text-sky-700 text-[10px] whitespace-nowrap" data-testid={testId}>RICHIESTA INVIATA</Badge>;
  return <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold whitespace-nowrap" data-testid={testId}>DA RICHIEDERE</Badge>;
};


// Solo admin: eccezione aziendale al pagamento anticipato per un cliente con plafond NON concesso
export const AdvanceExemptButton = ({ customer, info, onChanged }) => {
  const [saving, setSaving] = useState(false);
  if (!info || !(info.insurance_status === "refused" || info.insurance_status === "revoked")) return null;
  const exempt = !!info.advance_payment_exempt;
  const toggle = async () => {
    const msg = exempt
      ? `Ripristinare l'obbligo di PAGAMENTO ANTICIPATO per ${customer.business_name}?\nGli ordini aperti verranno di nuovo bloccati.`
      : `Esentare ${customer.business_name} dal pagamento anticipato (eccezione aziendale)?\nGli ordini aperti verranno sbloccati e i nuovi ordini non saranno bloccati, anche dopo i prossimi import Coface.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/customers/${customer.id}/advance-payment-exempt`, { exempt: !exempt });
      toast.success(exempt ? `Obbligo ripristinato (${r.data.orders_updated} ordini ri-bloccati)` : `Cliente esentato (${r.data.orders_updated} ordini sbloccati)`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={toggle}
      disabled={saving}
      className={`h-6 mt-1 text-[10px] px-2 ${exempt ? "border-red-300 text-red-700 hover:bg-red-50" : "border-amber-400 text-amber-800 hover:bg-amber-50"}`}
      data-testid={`advance-exempt-btn-${customer.id}`}
    >
      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : exempt ? "Ripristina obbligo" : "Esenta (eccezione)"}
    </Button>
  );
};

export const PlafondRequestDialog = ({ customer, open, onClose, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setNote(""); } }, [open]);
  const send = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/customers/${customer.id}/plafond-request`, { amount: amount ? parseFloat(amount) : null, note });
      toast.success("Richiesta plafond inviata all'amministrazione");
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSaving(false); }
  };
  if (!customer) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="plafond-request-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#8A1C2A]" />Richiedi plafond</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600 -mt-1">Cliente: <b>{customer.business_name}</b> — la richiesta arriva all'amministrazione, che la inoltrerà all'assicurazione.</p>
        <div className="space-y-2">
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Importo suggerito € (opzionale)" data-testid="plafond-req-amount" />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Nota (es. cliente nuovo, ordini previsti…)" data-testid="plafond-req-note" />
        </div>
        <Button onClick={send} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6e1622] w-full" data-testid="plafond-req-send">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Invia richiesta all'admin
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const ImportInsuranceDialog = ({ open, onClose, onDone }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState({});
  const [creating, setCreating] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setReport(null); setCreated({}); } }, [open]);
  const upload = async (f) => {
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/customers/import-insurance`, fd);
      setReport(r.data);
      setCreated({});
      toast.success(`${r.data.updated.length} clienti aggiornati`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setLoading(false); }
  };
  const createSubjects = async (subjects, key) => {
    setCreating(key);
    try {
      const r = await axios.post(`${API}/customers/create-from-insurance`, { subjects });
      const map = {};
      (r.data.created || []).forEach((c) => { map[c.vat] = c.codice_cliente; });
      (r.data.skipped || []).forEach((s) => { toast.info(`${s.name}: ${s.reason}`); });
      setCreated((prev) => ({ ...prev, ...map }));
      if ((r.data.created || []).length > 0) {
        toast.success(`${r.data.created.length} clienti creati in anagrafica con plafond applicato`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione");
    } finally { setCreating(null); }
  };
  const pendingSubjects = (report?.not_found || []).filter((u) => !created[u.vat]);
  const stLabel = { granted: ["CONCESSO", "text-emerald-700"], refused: ["NON CONCESSO", "text-red-600"], revoked: ["REVOCATO", "text-red-600"] };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col" data-testid="insurance-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#8A1C2A]" />Importa plafond assicurazione (Coface)</DialogTitle></DialogHeader>
        {!report && (
          <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center">
            <p className="text-sm text-stone-600 mb-1">Carica l'export Excel <b>RISK_PORTFOLIO</b> generato dal portale dell'assicurazione.</p>
            <p className="text-xs text-stone-400 mb-4">Match per P.IVA / Codice Fiscale · "Concesso" aggiorna il plafond · "Refused"/"Revocato" → pagamento anticipato</p>
            <Button onClick={() => fileRef.current?.click()} disabled={loading} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="insurance-upload-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Scegli file .xlsx
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="insurance-file-input" />
          </div>
        )}
        {report && (
          <div className="overflow-y-auto flex-1 space-y-3 text-sm">
            <p data-testid="insurance-report-summary"><b>{report.updated.length}</b> clienti aggiornati su <b>{report.subjects}</b> soggetti nel file ({report.total_rows} righe).</p>
            {report.updated.length > 0 && (
              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-64 overflow-y-auto">
                {report.updated.map((u, i) => (
                  <div key={i} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                    <span className="flex-1 font-medium truncate">{u.name}</span>
                    <span className={`font-bold ${stLabel[u.status]?.[1]}`}>{stLabel[u.status]?.[0]}</span>
                    {u.status === "granted" && <span className="font-mono">{fmEur(u.amount)}</span>}
                    <span className="text-stone-400">{fmDate(u.date)}</span>
                  </div>
                ))}
              </div>
            )}
            {report.not_found.length > 0 && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-amber-700 font-semibold flex-1">{report.not_found.length} soggetti del file senza cliente corrispondente in anagrafica:</p>
                  {pendingSubjects.length > 0 && (
                    <Button size="sm" onClick={() => createSubjects(pendingSubjects, "all")} disabled={creating !== null} className="bg-[#8A1C2A] hover:bg-[#6e1622] h-7 text-xs" data-testid="plafond-create-all-btn">
                      {creating === "all" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}
                      Crea tutti ({pendingSubjects.length})
                    </Button>
                  )}
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded-lg divide-y divide-amber-100 max-h-64 overflow-y-auto">
                  {report.not_found.map((u, i) => (
                    <div key={i} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate">{u.name}</span>
                      <span className="font-mono text-stone-500">{u.vat}{u.cf && u.cf !== u.vat ? ` · CF ${u.cf}` : ""}</span>
                      {created[u.vat] ? (
                        <span className="text-emerald-700 font-semibold flex items-center gap-1" data-testid={`plafond-created-${u.vat}`}><Check className="w-3.5 h-3.5" /> {created[u.vat]}</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => createSubjects([u], u.vat)} disabled={creating !== null} className="h-6 px-2 text-[11px] border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5" data-testid={`plafond-create-${u.vat}`}>
                          {creating === u.vat ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserPlus className="w-3 h-3 mr-1" /> Crea</>}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500">Con "Crea" il soggetto viene inserito in Anagrafica Clienti con i dati del file (ragione sociale, P.IVA/CF, indirizzo, telefono) e il plafond viene applicato subito. Potrai poi completare la scheda (agente, listino, email).</p>
              </>
            )}
            <Button variant="outline" onClick={() => setReport(null)} data-testid="insurance-import-again">Importa un altro file</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PlafondRequestsDialog = ({ open, onClose }) => {
  const [reqs, setReqs] = useState(null);
  const [error, setError] = useState(false);
  const load = () => {
    setError(false);
    axios.get(`${API}/plafond-requests`).then((r) => setReqs(r.data)).catch(() => { setReqs(null); setError(true); });
  };
  useEffect(() => { if (open) { setReqs(null); load(); } }, [open]);
  const markDone = async (r) => {
    try {
      await axios.put(`${API}/plafond-requests/${r.id}`, { status: "done" });
      toast.success("Richiesta segnata come gestita");
      load();
    } catch { toast.error("Errore"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="plafond-requests-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#8A1C2A]" />Richieste plafond dagli agenti</DialogTitle></DialogHeader>
        <div className="overflow-y-auto flex-1 space-y-2">
          {error && (
            <div className="text-sm text-red-600 p-4 text-center" data-testid="plafond-req-error">
              Errore nel caricamento delle richieste.
              <Button size="sm" variant="outline" onClick={load} className="ml-2 border-red-300 text-red-700" data-testid="plafond-req-retry">Riprova</Button>
            </div>
          )}
          {!error && reqs === null && <p className="text-sm text-stone-400 p-4 flex items-center gap-2" data-testid="plafond-req-loading"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</p>}
          {reqs?.length === 0 && <p className="text-sm text-stone-400 p-6 text-center" data-testid="plafond-req-empty">Nessuna richiesta.</p>}
          {reqs?.map((r) => (
            <div key={r.id} className={`border rounded-lg p-3 flex flex-wrap items-center gap-x-3 gap-y-1 ${r.status === "pending" ? "border-amber-300 bg-amber-50" : "border-stone-200 opacity-70"}`} data-testid={`plafond-req-${r.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{r.customer_name} <span className="font-mono text-xs text-stone-400">{r.customer_vat}</span></p>
                <p className="text-xs text-stone-500">da {r.requested_by} · {fmDate(r.created_at)}{r.note ? ` — "${r.note}"` : ""}</p>
              </div>
              {r.amount != null && <span className="font-mono font-bold text-sm">{fmEur(r.amount)}</span>}
              {r.status === "pending" ? (
                <Button size="sm" variant="outline" onClick={() => markDone(r)} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid={`plafond-req-done-${r.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Segna gestita
                </Button>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">GESTITA</Badge>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
